export interface CourseDetail {
  slug: string;
  name: string;
  programme: "black-belt" | "factory-skills" | "professional-proficiency" | "sustainability" | "internpro" | "ppi-workshop" | "other-training";
  programmeLabel: string;
  description: string;
  topics: string[];
  duration: string;
  audience: string;
  methodology?: string;
  includes?: string[];
  fee?: string;
}

/* ─── Helpers ─── */
function s(name: string, prefix: string): string {
  return `${prefix}-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}

/* ═══════════════════════════════════════════════════════════════
   Black Belt — 12 specialised innovation courses
   ═══════════════════════════════════════════════════════════════ */
export const blackBeltCourses: CourseDetail[] = [
  {
    slug: s("Printing Digital Printing", "bb"),
    name: "Printing (Digital Printing)",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Master advanced digital textile printing technologies including sublimation, reactive, and pigment ink systems. This course covers colour management, RIP software configuration, print head technology, and integration of digital printing into existing production workflows for rapid sampling and short-run production.",
    topics: [
      "Colour management and ICC profiling for textiles",
      "RIP software and print queue optimisation",
      "Sublimation, reactive, and pigment ink systems",
      "Workflow integration with CAD and sampling departments",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Fully Fashioned Garments", "bb"),
    name: "Fully Fashioned Garments",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Explore seamless knitting technology and whole-garment production methods that eliminate cut-and-sew operations. Participants master 3D knitting patterns, yarn selection for different garment zones, and the industrial engineering required to transition from conventional to fully fashioned manufacturing.",
    topics: [
      "Seamless knitting machine programming and pattern design",
      "Yarn engineering for zone-specific performance",
      "3D-to-2D pattern mapping and shape engineering",
      "Cost modelling and line transition strategy",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Protective Clothing", "bb"),
    name: "Protective Clothing",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Develop expertise in technical standards, functional fabric engineering, and safety compliance for protective apparel. This course addresses ballistic, thermal, chemical, and high-visibility protection requirements with a focus on Bangladesh's growing technical textiles market.",
    topics: [
      "International protective clothing standards (EN, ASTM, ISO)",
      "Functional fabric selection and lamination techniques",
      "Testing protocols and certification pathways",
      "Market positioning for technical textile exports",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Garment Washing Dyeing", "bb"),
    name: "Garment Washing & Dyeing",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Advanced garment washing and eco-dyeing techniques that balance aesthetic innovation with environmental responsibility. Participants learn enzymatic washing, ozone treatment, laser finishing, and low-liquor-ratio dyeing systems that reduce water and chemical consumption.",
    topics: [
      "Enzymatic, ozone, and laser finishing technologies",
      "Low-liquor-ratio and cold-batch dyeing systems",
      "Water recycling and effluent treatment integration",
      "Aesthetic trend forecasting and wash recipe development",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("3D Sampling and Industry 4.0", "bb"),
    name: "3D Sampling and Industry 4.0",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Lead the digital transformation of sampling through 3D virtual prototyping, digital twins, and smart manufacturing integration. This course covers CLO3D and Browzwear workflows, automated pattern translation, and how Industry 4.0 pillars connect in a Bangladesh factory context.",
    topics: [
      "3D virtual sampling with CLO3D and Browzwear",
      "Digital twin architecture for production lines",
      "IoT sensor integration and real-time monitoring",
      "ROI modelling for digital sampling adoption",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Denim Development", "bb"),
    name: "Denim Development",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Drive denim innovation through sustainable fabric development, advanced washing techniques, and circular economy models. Participants study stretch and rigid denim engineering, eco-stone alternatives, indigo reduction chemistry, and the design-to-market cycle for global denim brands.",
    topics: [
      "Stretch and rigid denim fabric engineering",
      "Eco-stone, foam, and laser wash alternatives",
      "Indigo reduction chemistry and dye recovery",
      "Circular design and post-consumer recycling",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Chemical Management Environment Sustainability", "bb"),
    name: "Chemical Management & Environment Sustainability",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Architect enterprise-level chemical management and environmental sustainability systems aligned with ZDHC, OEKO-TEX, and buyer requirements. This course covers chemical inventory digitisation, MRSL compliance auditing, wastewater treatment innovation, and circular economy strategy for textile factories.",
    topics: [
      "ZDHC MRSL compliance and chemical inventory digitisation",
      "Wastewater treatment innovation and ZLD pathways",
      "Carbon footprint measurement and reduction strategy",
      "Circular economy business models for textile factories",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Application of Lean and Industrial Engineering", "bb"),
    name: "Application of Lean and Industrial Engineering",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Apply lean and industrial engineering at the strategic level to architect factory-wide productivity transformation. This course goes beyond tools to cover governance design, kaizen culture building, automated line balancing, and predictive capacity planning using real factory datasets.",
    topics: [
      "Lean governance design and kaizen culture architecture",
      "Automated line balancing and predictive planning",
      "Value stream mapping at factory-wide scale",
      "Lean maturity assessment and transformation roadmaps",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Digital Marketing Merchandising", "bb"),
    name: "Digital Marketing & Merchandising",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Design and execute digital marketing and online merchandising strategies for apparel brands and B2B factory positioning. Participants develop e-commerce architectures, social commerce campaigns, data-driven buyer targeting, and content systems that convert digital presence into commercial outcomes.",
    topics: [
      "E-commerce architecture and platform selection",
      "Social commerce and influencer marketing for apparel",
      "Buyer targeting through data-driven advertising",
      "Content systems and brand storytelling at scale",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Fabric Technology", "bb"),
    name: "Fabric Technology",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Advance fabric technology knowledge from construction to functionalisation. This course covers advanced weaving and knitting structures, smart textile integration, moisture management, antimicrobial finishing, and testing protocols that meet international performance standards.",
    topics: [
      "Advanced fabric construction and structural engineering",
      "Smart textile integration and conductive yarn systems",
      "Functional finishing: moisture management, antimicrobial, UV",
      "Performance testing to ASTM, AATCC, and ISO standards",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Apparel Marketing Merchandising", "bb"),
    name: "Apparel Marketing & Merchandising",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Strategic apparel marketing and merchandising for global market penetration. This course addresses brand positioning, collection planning, buyer relationship management, trend analysis, and omnichannel merchandising strategies that connect Bangladesh production to international retail.",
    topics: [
      "Global brand positioning and competitive differentiation",
      "Collection planning and seasonal line architecture",
      "Buyer relationship management and negotiation strategy",
      "Omnichannel merchandising and retail analytics",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
  {
    slug: s("Supply Chain Management", "bb"),
    name: "Supply Chain Management",
    programme: "black-belt",
    programmeLabel: "Innovation Black Belt",
    description:
      "Architect resilient, transparent, and efficient apparel supply chains from raw material to retail shelf. This course covers end-to-end supply chain design, vendor risk management, logistics optimisation, digital traceability, and agile planning methods for volatile global markets.",
    topics: [
      "End-to-end supply chain design and network optimisation",
      "Vendor risk assessment and dual-sourcing strategy",
      "Digital traceability and blockchain integration",
      "Agile planning for demand volatility and disruption",
    ],
    duration: "12 hours",
    audience: "Senior managers, innovation officers, and technical heads",
  },
];

/* ═══════════════════════════════════════════════════════════════
   Factory Skills — 22 technical and operational courses
   ═══════════════════════════════════════════════════════════════ */
export const factorySkillsCourses: CourseDetail[] = [
  {
    slug: s("Lean Manufacturing", "fsd"),
    name: "Lean Manufacturing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "With rising manufacturing costs and ever-reducing buyer prices, the apparel industry faces the toughest challenge of maintaining profitability. 'Lean Manufacturing Process' is a proven vehicle to remain competitive. This course shares how factories have successfully overcome major challenges using lean — with crystal-clear, proven steps from experienced trainers. Includes guided factory floor demonstration and hands-on practice.",
    topics: [
      "History of lean, basic principles, and 14 principles of lean thinking",
      "5S workplace organisation, visual management and audit",
      "VA/NVA analysis, 3M framework, waste identification and elimination",
      "Value stream mapping: current state and future state",
      "Kaizen and kaizen breakthrough methodology",
      "Lean production system: Takt-Flow-Pull, JIT, JIDOKA, lean line concept",
      "Basic problem solving: RCA, 5-Why method, Pareto, fishbone, mistake proofing",
    ],
    duration: "2 days",
    audience: "Textile and garment company owners and management; professionals in Production/IE/Planning; merchandisers; persons seeking careers in lean manufacturing; fresh graduates and students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 3,000/-",
  },
  {
    slug: s("Implementing Sustainability Through 3R Innovation System Approach", "fsd"),
    name: "Implementing Sustainability Through 3R & Innovation System Approach",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Bangladesh's Department of Environment now mandates factories to submit a 3R plan for environment clearance renewal every year. 3R means Reduce, Reuse and Recycle — the factory's declaration of how it will move towards Zero Liquid Discharge. This training is specifically designed for knit dyeing & finishing factories to chart out 3R opportunities and implement them appropriately, reducing cost and improving occupational health, safety and the environment.",
    topics: [
      "Best available technologies and processes to reduce utilities and materials consumption",
      "Best available technologies and processes for reuse of utilities and materials",
      "Best available technologies and processes for recycling in textile operations",
      "Impact of 3R on organisational growth and competitive advantage",
      "3R policy and Environment Management System (EMS) design for factories",
      "Cost reduction through waste minimisation and alternative value-added use of resources",
      "Operational process redesign ensuring occupational safety and hazard control",
      "Case study: 3R opportunities in knit dyeing & finishing factories",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Supervisors from knit textile dye-houses, dyes & chemical manufacturers & suppliers; machinery or advisory service providers",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of workshop",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Garments Washing Effects", "fsd"),
    name: "Garments Washing & Effects",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Garments washing is a technology applied to modify the outlook, appearance, comfort and fashion of garments after stitching. With buyer-driven demand for vintage effects, worn-out looks and unique appearances growing rapidly, proper knowledge of washing processes is essential for anyone working in the textile sector. This course covers the full spectrum of wash types, processes, sustainable techniques, costing and testing — with a factory floor demonstration on Day 4 at a professional washing facility.",
    topics: [
      "Definition, purpose and processes of washing: fashion, value addition and sustainability",
      "Origin of washing concepts, wet process machines and dry process equipment",
      "Wash types and flow chart of wash processes",
      "Types of fabric used in washing and history of denim",
      "Different washing effects on denim and GMT dyeing",
      "Discussion on practical wash processes and trouble shooting",
      "Sustainable techniques: SWEPT concept and conventional process comparison",
      "Detailed costing procedures and test parameters",
      "Factory floor demonstration at professional washing facility (Day 4)",
    ],
    duration: "2 days",
    audience: "Owners, Directors, GMs, Managers, Merchandisers, Executives working in garments washing, product development, merchandising, and related fields",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Trends Techniques of Garments Printing", "fsd"),
    name: "Trends & Techniques of Garments Printing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Bangladesh loses numerous print orders each year due to absence of proper process knowledge, skilled manpower and technological development. This intensive 2-day training — conducted jointly by Textile Today and DYSIN — gives professionals hands-on experience on garment printing process, techniques and new scopes. Day 1 is classroom at DYSIN Conference Center; Day 2 is a live printing demonstration and workshop in Mirpur.",
    topics: [
      "Various printing techniques: history and modernisation",
      "History of screen printing and pre-press details",
      "Water base printing including fashionable effects",
      "Plastisol printing and its effectiveness",
      "Solvent base printing scope in Bangladesh",
      "Effects with silicone printing agents",
      "Sublimation transfer printing using digital plotters and heat press",
      "Digital printing on cotton",
      "RSL (Restricted Substance List) of brands in brief",
      "RSL matter associated with each type of prints",
      "Hands-on printing demonstration and workshop (Day 2, Mirpur)",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Supervisors from textile printing industry; dyes, chemicals, machinery or advisory service providers of the garment printing industry",
    methodology: "PowerPoint Presentation, Hands-on Demonstration of Techniques, Question and Answer session",
    includes: [
      "Course materials and food provided by organizers",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("International Trade Export-Import Commercial Management", "fsd"),
    name: "International Trade & Export-Import Commercial Management",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "In today's global economy, navigating the complexities of international trade and import-export management is essential for textile and apparel professionals. This course provides a comprehensive understanding of international trade procedures — from modes of payment and LC operations to Incoterms, insurance and risk management — equipping participants to expand their business globally with confidence.",
    topics: [
      "Introduction to international trade: roles of agent, trader and manufacturer",
      "GDP and modes of payment in international trade",
      "Import and export procedures",
      "Letter of Credit (LC) types, terms, opening and documentation",
      "Incoterms selection and application",
      "Insurance, H.S. code, tariff rules and custom procedures",
      "Foreign exchange rate and SWIFT — commonly used SWIFT messages",
      "Overview of UCPDC-600",
      "Off-shore banking and trade services of banks in Bangladesh",
      "Trade negotiation and risk management",
      "Cash incentive, customs audit and practical approaches for opening different types of LC",
      "Trade license, ERC, IRC and other licenses required",
    ],
    duration: "2 days",
    audience: "Manufacturers, traders, agents, merchandisers and executives involved in international apparel trade and commercial management",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,990/-",
  },
  {
    slug: s("Tools and Techniques of Implementing Lean in Apparel Industry", "fsd"),
    name: "Tools and Techniques of Implementing Lean in Apparel Industry",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "This advanced lean course shares proven steps on how factories have successfully increased productivity using lean tools specifically adapted for apparel manufacturing. Going beyond the basics, this programme covers Kanban, Value Stream Mapping, Standard Work, Quick Changeover (SMED) and structured problem-solving approaches with practical exercises throughout.",
    topics: [
      "Kanban: card data, benefits, how it works, six principles, kanban calculation",
      "VSM (Value Stream Mapping): current state, lean value stream, future state, achieving the future state",
      "Standard Work: importance, Takt Time, Cycle Time, line balancing, work sequence, SWIP, SOP",
      "Tools: Time Observation Form, process capacity calculation, standard operation sheets",
      "Structured problem solving: 8D method, RCA (5W, 80/20, C&E), Poka Yoke",
      "Quick Changeover (SMED): advantages, 3 Ups for changeover, keys and steps to attack changeover",
    ],
    duration: "2 days",
    audience: "Textile and garment company owners and management; professionals in Production/IE/Planning; merchandisers; persons seeking careers in lean manufacturing; fresh graduates and students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of workshop",
    ],
    fee: "BDT 3,000/-",
  },
  {
    slug: s("Product Development", "fsd"),
    name: "Product Development",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Bangladesh is the 2nd largest garment exporter in the world, but Vietnam and China are fierce competitors. To secure new orders, suppliers must master product development. This 4-session course covers the entire NPD process from fashion forecasting and raw material selection to pattern making, printing techniques, washing effects, 3D digital technology and a practical sample-making session in a sample section.",
    topics: [
      "Product development process and understanding fashion & fashion markets",
      "Theory of fashion change, colour trend forecasting and fashion supply chain",
      "Understanding vendor pack and basic textile raw materials (fibre, yarn, fabric)",
      "Fabric structure design analysis, anthropometric data and garment pattern understanding",
      "Pattern making and marker making using CAD; fabric consumption calculation",
      "Different printing techniques, washing effects and embroidery techniques",
      "Stitches, seams, trims and accessories; sample making stages and quality fault remedies",
      "Digital product development and 3D technology; fashion information sources",
      "Practical session: tour of sample section, selecting style, pattern making, cutting, print execution, sewing and finishing",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Apparel Marketing", "fsd"),
    name: "Apparel Marketing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "To survive and achieve regular growth in business, apparel professionals need efficient marketing skills beyond technical knowledge. This course covers the full spectrum of apparel marketing — from personal branding and market analysis to strategy execution and after-sales services — with a focus on Bangladesh's RMG export context, buyer identification, and practical business development tools.",
    topics: [
      "Introduction to apparel marketing and personal branding qualities of a marketer",
      "Defining relation between merchandising and marketing; buyers, suppliers and agents",
      "Bangladesh RMG industry services and products overview",
      "Objective, goal, vision, mission and creating your own identity",
      "Target customers, present market scenario and consumer behaviour",
      "International retailers and fashion brands; fashion forecasting and trend understanding",
      "Traditional vs non-traditional markets; focus on Bangladesh market (buying houses, agents, liaison offices)",
      "Marketing strategies: pricing, negotiation, market segmentation, SWOT, deep buyer knowledge",
      "Finance, budget and forecast; ways of marketing and communication tools",
      "Real buyer identification system, buying meeting and execution",
      "Follow-up, feedback collection, strategy update and after-sales services",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Students; professionals in merchandising and marketing",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Weaving Woven Processing", "fsd"),
    name: "Weaving & Woven Processing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "As China reduces textile output to control environmental pollution, Bangladesh needs to capture redirected orders — and that means achieving world-class standards in woven fabric production. This course covers the complete weaving and woven processing value chain: from fibre and yarn selection through loom technology, fabric structure design, woven dyeing and finishing, all the way to costing and quality parameters.",
    topics: [
      "Types of fibre, fibre parameters, usage, processing and details",
      "Types of yarn and yarn production processes in Bangladesh spinning industry",
      "Discussion on yarn parameters, control techniques and their impact on weaving",
      "Principles of weaving and constructional design of weaving and woven fabrics",
      "Colour and weave effects, fabric analysis, nonwoven fabric bonding techniques",
      "Weaving modification techniques and loom technique operations",
      "Typical fault conditions, fault finding procedures and trouble shooting",
      "Costing of yarn and fabric",
      "Woven dyeing techniques, critical points and dyeing costing details",
      "Different test parameters and procedures",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Denim Development Manufacturing", "fsd"),
    name: "Denim Development & Manufacturing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "In fast-fashion, denim definition and market trends change faster than ever. Understanding denim development — from fabric R&D to wash processes — is critical for anyone in the denim fraternity. This course covers denim development strategy, raw material selection, process design, yarn structure analysis, manufacturing technologies, and specialised finishing treatments that create premium denim products.",
    topics: [
      "Target of denim development and research & development of denim",
      "Denim development plan and procedure",
      "Wash process of denim garments and new development trends",
      "Denim by weave and yarn structure analysis",
      "Processes involved in denim fabric manufacturing",
      "Types of warp yarn preparation and sizing",
      "Technologies in denim including rope and sheet dyeing systems",
      "Denim weaving and specialised finishing and distressing techniques",
      "Market trends and denim fashion forecasting",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Merchandisers, Executives, R&D professionals and students related to the denim industry",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Total Quality Management", "fsd"),
    name: "Total Quality Management",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Quality is the main criteria for success in Bangladesh's internationally competitive textile and garment sector. TQM integrates all organisational functions to focus on meeting customer demands and organisational objectives — doing things right the first time and eradicating defects and waste. This course covers advanced quality management concepts of kaizen and TQM to ensure optimum quality products by reducing waste, manufacturing cost and improving resource efficiency.",
    topics: [
      "Principals of Total Quality Management",
      "Root cause analysis, key issues prioritisation and solutions",
      "Introduction of zero defect quality and its necessity",
      "Basic components analysis of zero defect quality",
      "Importance of kaizen in quality assurance",
      "Methods for mistake proofing process (Poka Yoke)",
      "Predictable and non-predictable conditions which lead to defects",
      "Evaluation of modern quality systems",
    ],
    duration: "2 days",
    audience: "Textile and garment company owners and management; professionals in Production/IE/Planning; merchandisers; fresh graduates and students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of workshop",
    ],
    fee: "BDT 3,000/-",
  },
  {
    slug: s("Techniques of All Over Printing AOP Processing", "fsd"),
    name: "Techniques of All Over Printing (AOP) Processing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "All-over printing (AOP) is the most rapidly growing textile industry segment in Bangladesh with 85+ factories now running AOP techniques. This course covers the full spectrum from rotary screen and flatbed printing to the new wave of all-over digital printing — including parameters, process flow charts, challenges, trouble shooting, and testing requirements.",
    topics: [
      "Definition and classification of fabric printing",
      "Types of printing: Pigment, Reactive, Discharge, Raised, Glitter, Burn Out prints",
      "Parameters of all-over printing: screen pressure, design setting, shade",
      "Process flow chart and elaborate discussion",
      "Detailed discussion on Rotary Screen Printing",
      "Detailed discussion on Flat Bed Printing",
      "All-over digital printing: technology, advantages and growing demand",
      "Challenges and trouble shooting in AOP operations",
      "Testing requirements and precautions",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Quality Control Inspection", "fsd"),
    name: "Quality Control & Inspection",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Quality control at different stages of production is vital to ensure the quality of the final product. Quality professionals must know international quality standards for different buyers and the proper sequence of inspection to meet buyer demands. This course trains participants to be efficient quality control professionals capable of handling quality parameters, inspections and inventory management — including a factory visit to Noman Terry Group.",
    topics: [
      "Making sure AQL (Acceptable Quality Level) and Pantone Book study",
      "Understanding Technical Sheet / BOM (Bill of Material) Sheet",
      "Fabric quality ensuring process: knitting, lab dip, dyeing, finishing",
      "4-point system fabric inspection",
      "Label approval procedure: main label, care label, size label, patch",
      "Understanding print quality, embroidery quality, sequence quality",
      "Understanding zipper, velcro and elastic quality and types",
      "Inventory process: fabrics and accessories",
      "SCOTA files preparation and inspection procedure",
      "Pilot run, inline inspection, pre-final and final inspection; inspection room arrangement",
      "Understanding test parameters and sample checking procedures (development, size set, PP, salesman samples)",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Merchandisers, Executives, Students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Apparel Supply Chain Management", "fsd"),
    name: "Apparel Supply Chain Management",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "The apparel industry stands out as one of the most globalized industries in the world and it is a supply driven commodity chain led by a combination of retailers, contractors, subcontractors, merchandisers, buyers, and suppliers; each plays an important role in a network of supply chains that spans from fibers to yarn, to fabrics, to accessories, to garments, to trading, and to marketing. So Apparel Supply Chain is denoted as a system with the full range of activities that are required to bring a cloth from conception (i.e. product design and development), through the different phases of production (i.e. the physical transformation of raw materials to finished product outputs), delivery to final consumers (i.e. sales and distribution), and final disposal after use (i.e. consumption / recycling). In maximum case apparel manufacturing organization is harassed to make the downy supply chain in order to deliver garments to the buyer on time as well as gain the profit margin by reducing unnecessary waste from chain. In this regard depth knowledge on Apparel Supply Chain will help an individual, a firm or a country to analyze and identify its core competencies. An individual, a firm or a country can also plan holistically on an industry by locating what to do and what not to do. Clear understanding of the complexities of Apparel Supply Chain would build confidence among the people working in different stages of the supply chain. And this would help the whole sector grow together by maintaining harmony reducing risk.",
    topics: [
      "What is supply chain management and its objectives",
      "Importance and benefits of SCM in apparel manufacturing",
      "Decision phases of supply chain management",
      "Apparel supply chain process overview",
      "Case study on apparel supply chain dynamics",
      "Merchandising inquiry and material sourcing procedures",
      "Costing and consumption calculation for apparel orders",
      "Development and sampling workflow in supply chain",
      "Order confirmation and final booking processes",
      "Material in-house management and critical path analysis",
      "Bank related issues: PI, Master LC, BB LC, Incoterms, Payments, Contracts",
      "Commercial issues: LC Opening, Documentation requirements",
      "Transportation, clearing & forwarding, and shipping procedures",
      "Review of previous classes and practical exercises",
      "Supply chain practice of a brand: end-to-end case study",
      "Supply chain practice of a spinning factory",
      "Supply chain practice of a knit composite factory",
      "Supply chain practice of a woven factory",
      "Supply chain practice of a sweater factory",
      "Supply chain practice of a lingerie factory",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
  },
  {
    slug: s("Knitting Knit Processing", "fsd"),
    name: "Knitting & Knit Processing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Bangladesh is one of the largest knit garment exporters in the world, yet the industry faces continuous new challenges in knitting quality, machine management and productivity. This course shares practical problems and solutions, covering knitting basics through machine maintenance, defect prevention and efficiency optimisation — with a focus on maximising profit in an increasingly price-sensitive market.",
    topics: [
      "Basics of knitting, knitting types and chronological developments",
      "Raw material selection and its quality; risk assessment",
      "Importance and guidelines on traceability",
      "Elements of machine adjustment and impact of lycra percentage",
      "Composition of fleece and designed fabrics; analysis of quality reports",
      "Defect types and remedy; maintaining appropriate quality",
      "Value added product development",
      "Knitting machine maintenance: caring of needle, sinker, cam and cylinder",
      "7 wastage reduction in knitting and efficiency maximisation",
      "Key focus area to maximise profit in upcoming price-sensitive market",
      "Optimise inventory of grey yarn, needle, sinker, oil and consumables",
    ],
    duration: "2 days",
    audience: "Factory managers, supervisors, technicians, executives and students working in or aspiring to join the knit manufacturing sector",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 3,000/-",
  },
  {
    slug: s("Rotary Screen All Over Printing Design and Development", "fsd"),
    name: "Rotary Screen (All Over) Printing Design and Development",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "This specialised course covers the complete design development workflow for all-over and chest prints in rotary screen printing. Participants learn how to solve the most common development problems — from missing repeat measurements and unclear design files to colour separation and registration issues — with hands-on practical sessions using industry-standard software like Optitex and Lectra.",
    topics: [
      "All over print types: Rotary Screen, Flat Bed Screen, Table Print",
      "Difference between rotary and flat bed screen printing",
      "Problems and possibilities of AOP design development: file clarity, repeats, colour references, fabric specs",
      "Basic idea on design; difference between chest print and all-over print design",
      "Repeat design and calculation of repeat measurement; design position measurement",
      "Outline, motif, colour separation, setting mark, over maintain, half tone, big net spray on design",
      "Software and equipment: Optitex and Lectra; uses of tools and options",
      "Continuous steps on design (practical session)",
      "Classification of screen prints: rubber, pigment, block, discharge, glitter, puff, metallic, foil and more",
      "Classification of all-over prints: pigment, reactive, discharge, raised",
    ],
    duration: "2 days",
    audience: "Designers, CAD professionals, print technicians and production executives in the textile printing industry",
    methodology: "PowerPoint Presentation, Practical Design Sessions, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Basics of Web Designing for Digital Marketing", "fsd"),
    name: "Basics of Web Designing for Digital Marketing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "World trade is rapidly moving online and B2B trade is going digital. 39% of manufacturers already have an e-commerce site and this number is growing fast. In the digital marketplace, a company website is like a shop window — and decorating it effectively is critical. This course focuses on digital marketing fundamentals with practical sessions on Alibaba.com — the world's leading B2B marketplace with 10 million+ active buyers — covering company profile setup, product posting, and optimisation.",
    topics: [
      "Introduction to digital marketing and B2B e-commerce",
      "How to set up a compelling company profile on e-commerce platforms",
      "Company profile solution suggestions and optimisation",
      "How to add and manage premium products",
      "Product posting solution suggestions",
      "Optimising product title, keywords and images for search",
      "How to improve response rate",
      "What is a Star Supplier and why it matters",
      "Foreign trade process, import and export procedure",
      "How to do export by e-commerce",
    ],
    duration: "2 days",
    audience: "Businessmen, entrepreneurs, professionals and executives looking to expand into digital and e-commerce channels",
    methodology: "PowerPoint Presentation, Interactive session with relevant videos and case studies, and role plays",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 3,000/-",
  },
  {
    slug: s("Shifting Dynamics in Merchandising Skills to Adopt the Transformational Challenges", "fsd"),
    name: "Shifting Dynamics in Merchandising & Skills to Adopt the Transformational Challenges",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Navigate the changing landscape of apparel merchandising driven by fast fashion acceleration, digital buying, and sustainability demands. This course covers agile merchandising planning, digital showroom management, nearshoring considerations, and the skills merchandisers need for the next decade.",
    topics: [
      "Agile merchandising and rapid response planning",
      "Digital showroom and virtual sample presentation",
      "Nearshoring implications and supplier network shifts",
      "Sustainability-driven sourcing and certification tracking",
    ],
    duration: "2 days",
    audience: "Merchandisers, senior executives, managers and professionals working in apparel buying, sourcing and merchandising",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Industrial Engineering", "fsd"),
    name: "Industrial Engineering",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Industrial Engineers play a vital role in production planning & control, making production smooth and planning for future orders. This course covers the full IE function in apparel manufacturing — from style review and sample study through method study, line management, production monitoring and operator training — with practical training in an apparel factory included as part of the programme.",
    topics: [
      "Introduction to IE: definition, history, flow chart and benefits",
      "Upcoming style review: monthly projection, sample collection, style analysis",
      "Sample study: information collection, data collection, sample analysis, layout preparation",
      "Method study: sewing line methodology, layout development, workplace design, motion study, machine tools development",
      "Line management: operator management, machine management, line target setting, accessories checklist",
      "Production follow-up/monitoring: time study, process improvement, bottleneck identification, route cause analysis",
      "Operator evaluation: skill matrix, types of operation, improvement tracking",
      "Operator recruitment, training and development: 5S, 7 wastage, 7 habits, skill development, motivation",
      "Practical training in apparel industry with factory demonstrations",
    ],
    duration: "2 days",
    audience: "Owner, Director, GM, Managers, Executives, Students",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of workshop",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Textile Fibre Yarn Fabric", "fsd"),
    name: "Textile Fibre, Yarn, Fabric",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Fibre and yarn are the core of fabric. If quality and properties are not properly understood by pricing or production staff, proper pricing and quality fabric cannot be achieved. This 4-part course covers the complete raw materials value chain — from fibre classification to yarn manufacturing technologies to knit and woven fabric R&D — including new spinning technologies and future scope of sustainable materials.",
    topics: [
      "Introduction to textile fibres: classification (natural, man-made), physical properties, morphology",
      "Cost-effectiveness and consumption of different fabric types; machinery for different fibre types",
      "Textile fibre identification methods and testing equipment",
      "Yarn manufacturing: blowroom, carding, drawframe, simplex, ringframe; melt/dry/wet spinning",
      "Different spinning technologies in Bangladesh: ring, rotor, compact, air-jet, core spun",
      "Spandex yarn and its impact on fabric quality; spinning calculations",
      "Knitting: raw material selection, R&D of knitted fabrics, fabric costing and market challenges",
      "Knitting quality: QC Circle, 5S, Lean; automation and future scope of warp knit and seamless",
      "Research and development of woven fabrics; new development trends",
      "Machinery used for developing and manufacturing woven fabrics and technologies",
    ],
    duration: "2 days",
    audience: "Factory owners, managers, merchandisers, executives, technicians and students working across spinning, knitting, weaving and apparel manufacturing",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Knit Dyeing Finishing", "fsd"),
    name: "Knit Dyeing & Finishing",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "The dyeing and finishing industry has made a crucial contribution to Bangladesh's apparel growth, but faces ongoing challenges around resource scarcity, environmental issues, and cost pressures. This course trains participants to become efficient dyeing and finishing professionals — covering the full production cycle from laboratory recipe support through dyeing, finishing, quality testing, and resource-saving best practices.",
    topics: [
      "Laboratory support: recipe making and testing",
      "Batching and dyeing as per production plan",
      "Dyeing processes and machine operation",
      "Trouble shooting: common and complex dyeing defects and remedies",
      "Industrial finishing procedure and finishing chemical selection",
      "Product quality testing: handfeel, colour fastness and dimensional stability",
      "Saving water, energy and process time in dyeing operations",
      "Inspection and delivery as per plan",
    ],
    duration: "2 days",
    audience: "Managers, executives, supervisors and technicians working in knit dyeing, finishing and related textile processing",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 5,900/-",
  },
  {
    slug: s("Production Planning Control", "fsd"),
    name: "Production Planning & Control",
    programme: "factory-skills",
    programmeLabel: "Factory Skills Development",
    description:
      "Production planning and control (PPC) is of great importance for the competitive position of manufacturing firms. This 2-day course covers PPC concepts, terminology and practical implementation for apparel and textiles — from capacity planning and order scheduling through to Gantt chart-based planning and popular industry software like Fast React and DATATEX. Day 1 covers concepts and Day 2 delivers full hands-on factory-level implementation.",
    topics: [
      "Concept, definition and terminology: production, productivity, efficiency, TNA, critical path",
      "Role of PPC department: planner duties, responsibilities and KPIs",
      "Industry-specific focus: composite, vertically and horizontally integrated apparel manufacturers",
      "Basic IE concepts (SMV), capacity planning, order planning and resources planning",
      "Production scheduling and garments production planning and control",
      "Gantt chart-based planning; popular software: Fast React, DATATEX MCM & MQM",
      "Day 2 implementation: factory capacity plan, line loading plan, lead time allocation (TNA)",
      "Knitting, dyeing, cutting, printing, sewing and finishing sub-plans with priority management",
      "Production monitoring, bottleneck identification and schedule adherence",
    ],
    duration: "2 days",
    audience: "Production managers, planners, IE professionals, executives and students working in apparel manufacturing",
    methodology: "PowerPoint Presentation, Exercise, Question and Answer session",
    includes: [
      "Course materials and food provided by Textile Today",
      "Certificate awarded to participants at the end of training",
    ],
    fee: "BDT 3,000/-",
  },
];

/* ═══════════════════════════════════════════════════════════════
   Professional Proficiency — 31 management and soft-skill courses
   ═══════════════════════════════════════════════════════════════ */
export const professionalProficiencyCourses: CourseDetail[] = [
  {
    slug: s("Techniques of Effective Communication", "pp"),
    name: "Techniques of Effective Communication",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Develop professional communication skills including business writing, active listening, cross-cultural communication in buyer-supplier relationships, and presentation techniques. This course addresses the communication gaps that cause errors, delays, and misunderstandings in factory operations.",
    topics: [
      "Business writing for emails, reports, and technical documents",
      "Active listening and feedback techniques",
      "Cross-cultural communication with international buyers",
      "Presentation and meeting facilitation skills",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Negotiation Skill", "pp"),
    name: "Negotiation Skill",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Master negotiation techniques including win-win bargaining, BATNA analysis, contract negotiation with buyers and suppliers, and conflict resolution. Participants practice real scenarios from the Bangladesh RMG context including price, delivery, and quality standard negotiations.",
    topics: [
      "Win-win bargaining and interest-based negotiation",
      "BATNA, ZOPA, and reservation price analysis",
      "Contract negotiation for price, delivery, and quality",
      "Conflict resolution and difficult conversation management",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Stress Management Emotional Intelligence", "pp"),
    name: "Stress Management & Emotional Intelligence",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Build emotional intelligence and stress resilience for leadership roles in high-pressure factory environments. This course covers self-awareness, empathy, emotional regulation, mindfulness practices, and team wellbeing strategies that reduce turnover and improve morale.",
    topics: [
      "Self-awareness and emotional regulation frameworks",
      "Empathy and social skills for team leadership",
      "Mindfulness and stress reduction techniques",
      "Team wellbeing strategies and burnout prevention",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Visionary Conceptual Skill", "pp"),
    name: "Visionary (Conceptual) Skill",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Develop strategic and conceptual thinking capabilities essential for senior leadership. This course covers systems thinking, scenario planning, futures methodology, and the ability to translate abstract organisational goals into concrete operational plans that teams can execute.",
    topics: [
      "Systems thinking and causal loop mapping",
      "Scenario planning and futures methodology",
      "Strategy-to-operations translation techniques",
      "Vision communication and organisational alignment",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Recruitment Management", "pp"),
    name: "Recruitment Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Design and execute effective recruitment processes for textile and apparel factories. This course covers job analysis, sourcing channels, structured interviewing, assessment centre design, onboarding programmes, and the legal compliance requirements of Bangladesh labour law.",
    topics: [
      "Job analysis and competency framework development",
      "Sourcing channels and employer branding",
      "Structured interviewing and assessment techniques",
      "Onboarding design and legal compliance",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Relationship Marketing", "pp"),
    name: "Relationship Marketing",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Build long-term buyer and supplier relationships that go beyond transactional pricing. This course covers CRM principles, loyalty programme design, B2B networking strategy, account management, and the trust-building practices that secure repeat orders in competitive markets.",
    topics: [
      "CRM principles and customer lifetime value",
      "Loyalty programme design for B2B apparel relationships",
      "Account management and key buyer retention",
      "Trust-building and relationship repair strategies",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Management Information System", "pp"),
    name: "Management Information System",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Leverage management information systems for data-driven decision making in factory operations. This course covers MIS architecture, dashboard design, KPI tracking systems, ERP reporting, and the analytics skills needed to turn production data into actionable management insights.",
    topics: [
      "MIS architecture and data flow design",
      "Dashboard and KPI tracking system configuration",
      "ERP reporting and production data analysis",
      "Analytics for actionable management insights",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Commercial Banking Purchase Management", "pp"),
    name: "Commercial, Banking & Purchase Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Manage commercial, banking, and purchase operations critical to apparel manufacturing. This course covers purchase order management, banking documentation, LC operations, vendor payment terms, cash flow planning, and the commercial controls that protect factory profitability.",
    topics: [
      "Purchase order management and vendor negotiation",
      "Banking documentation and LC operations",
      "Vendor payment terms and cash flow planning",
      "Commercial controls and profitability protection",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Data Processing and Representation Using Excel Other Software", "pp"),
    name: "Data Processing and Representation Using Excel & Other Software",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Advance data processing and visualisation skills using Microsoft Excel and complementary software. This course covers advanced formulas, pivot tables, dashboard creation, Power BI basics, and the automated reporting systems that eliminate manual data entry in production tracking.",
    topics: [
      "Advanced Excel formulas, pivot tables, and macros",
      "Dashboard design and KPI visualisation",
      "Power BI basics and automated reporting",
      "Data validation and error-proofing techniques",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Mastering Life", "pp"),
    name: "Mastering Life",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "A holistic personal development programme that integrates work-life balance, health consciousness, financial literacy, and purposeful living. This course helps professionals design a sustainable lifestyle that supports both career ambition and personal wellbeing in the demanding textile industry.",
    topics: [
      "Work-life integration and boundary management",
      "Health consciousness and sustainable habits",
      "Financial literacy and personal wealth planning",
      "Purpose discovery and long-term life design",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Decision Making Problem Solving Techniques", "pp"),
    name: "Decision Making & Problem Solving Techniques",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Apply structured decision-making and problem-solving frameworks to management challenges in textile factories. This course covers root-cause analysis, decision trees, force-field analysis, Pareto principle, and the facilitation methods that engage teams in finding durable solutions.",
    topics: [
      "Root-cause analysis and fishbone diagramming",
      "Decision trees and risk-weighted evaluation",
      "Force-field analysis and Pareto principle application",
      "Team facilitation for collaborative problem solving",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Business Process Analysis", "pp"),
    name: "Business Process Analysis",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Analyse and redesign business processes to improve efficiency, reduce waste, and enhance output quality. This course covers process mapping, BPMN notation, value-added analysis, bottleneck identification, and the change management needed to implement process improvements successfully.",
    topics: [
      "Process mapping and BPMN notation",
      "Value-added and non-value-added activity analysis",
      "Bottleneck identification and throughput optimisation",
      "Change management for process redesign rollout",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Labor Law HR Regulation Training Need Assessment", "pp"),
    name: "Labor Law, HR Regulation & Training Need Assessment",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Navigate Bangladesh labour law, HR compliance requirements, and systematic training need assessment. This course covers the Labour Act 2006, factory regulation compliance, disciplinary procedures, and the TNA methods that identify skill gaps and justify training investment.",
    topics: [
      "Bangladesh Labour Act 2006 and amendment awareness",
      "Factory regulation compliance and audit readiness",
      "Disciplinary procedures and documentation requirements",
      "Training need assessment methods and gap analysis",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Digital Marketing", "pp"),
    name: "Digital Marketing",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Design and execute digital marketing strategies for apparel brands, factories, and service providers. This course covers SEO, SEM, social media marketing, content strategy, email marketing, and analytics — with a focus on the B2B and export marketing context of Bangladesh's textile sector.",
    topics: [
      "SEO and SEM fundamentals for apparel businesses",
      "Social media marketing and community building",
      "Content strategy and brand storytelling",
      "Email marketing, analytics, and campaign optimisation",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Strategic Management", "pp"),
    name: "Strategic Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Develop strategic management capabilities using SWOT, PESTLE, Porter's frameworks, and competitive strategy models. This course teaches participants to analyse industry structure, identify competitive advantages, allocate resources strategically, and formulate plans that align factory operations with market opportunities.",
    topics: [
      "SWOT, PESTLE, and Porter's Five Forces analysis",
      "Competitive strategy and differentiation positioning",
      "Resource allocation and strategic prioritisation",
      "Strategy execution and alignment mechanisms",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Supply Chain Management", "pp"),
    name: "Supply Chain Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Manage apparel supply chains from strategic perspective covering logistics, inventory optimisation, vendor relationship management, and risk mitigation. This course addresses the unique challenges of Bangladesh's RMG supply chain including raw material lead times, freight volatility, and compliance coordination.",
    topics: [
      "Logistics optimisation and freight consolidation",
      "Inventory management and working capital reduction",
      "Vendor relationship management and performance scoring",
      "Risk mitigation for supply disruption and compliance",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Business English", "pp"),
    name: "Business English",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Strengthen business English proficiency for professional interactions with international buyers, auditors, and partners. This course covers email etiquette, meeting vocabulary, presentation language, report writing, and the cultural nuances that make communication effective in global apparel business.",
    topics: [
      "Professional email etiquette and tone management",
      "Meeting vocabulary and discussion facilitation",
      "Presentation language and slide narrative design",
      "Report writing and cross-cultural communication nuance",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Goal Setting", "pp"),
    name: "Goal Setting",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Master goal-setting frameworks including SMART goals, OKRs, and cascade planning that connect individual performance to departmental and organisational objectives. This course teaches participants to set motivating targets, track progress, and adjust goals based on changing factory priorities.",
    topics: [
      "SMART goal framework and objective clarity",
      "OKRs and cascade planning for alignment",
      "Progress tracking and milestone management",
      "Goal adjustment and priority recalibration methods",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Team Building", "pp"),
    name: "Team Building",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Build cohesive, high-performing teams in factory and office environments. This course covers team formation stages, role clarification, trust-building exercises, conflict resolution, and the leadership behaviours that foster collaboration across departments in textile organisations.",
    topics: [
      "Team formation stages and role clarification",
      "Trust-building and psychological safety practices",
      "Cross-departmental collaboration facilitation",
      "Conflict resolution and team cohesion maintenance",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("SOP and Organization Development", "pp"),
    name: "SOP and Organization Development",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Develop standard operating procedures and organisation development interventions that create consistency and enable scale. This course covers SOP writing methodology, process documentation, OD diagnostic tools, change management frameworks, and the rollout methods that ensure adoption across production floors.",
    topics: [
      "SOP writing methodology and process documentation",
      "OD diagnostic tools and intervention design",
      "Change management frameworks for factory adoption",
      "Rollout planning and compliance monitoring systems",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Performance Management Appraisal", "pp"),
    name: "Performance Management & Appraisal",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Design and run performance management systems that motivate employees and align individual contribution with factory goals. This course covers KPI design, 360-degree feedback, appraisal interview techniques, performance-based compensation links, and the documentation that protects both employer and employee.",
    topics: [
      "KPI design and measurement system architecture",
      "360-degree feedback and multi-source assessment",
      "Appraisal interview techniques and coaching conversation",
      "Performance-based compensation and documentation",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Sales Management Succession Planning for Sales", "pp"),
    name: "Sales Management & Succession Planning for Sales",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Lead sales teams and build succession pipelines for commercial roles in apparel manufacturing and trading. This course covers sales strategy, pipeline management, target setting, coaching techniques, talent identification, and the development plans that ensure continuity when key salespeople transition.",
    topics: [
      "Sales strategy and pipeline management",
      "Target setting and incentive scheme design",
      "Sales coaching and performance improvement",
      "Succession planning and talent continuity frameworks",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Financial Management", "pp"),
    name: "Financial Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Develop financial literacy and management skills for non-finance professionals in textile organisations. This course covers budgeting, cash flow forecasting, financial statement reading, cost centre management, break-even analysis, and the financial controls that protect factory margins.",
    topics: [
      "Budgeting and variance analysis for production departments",
      "Cash flow forecasting and working capital management",
      "Financial statement reading and ratio analysis",
      "Cost centre management and break-even calculation",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Change Management", "pp"),
    name: "Change Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Lead organisational change effectively using established frameworks including Kotter's 8-step model, ADKAR, and the change curve. This course addresses resistance management, communication planning, stakeholder engagement, and the metrics that track whether a change initiative is actually taking hold.",
    topics: [
      "Kotter's 8-step change model and ADKAR framework",
      "Resistance identification and mitigation strategies",
      "Stakeholder mapping and communication planning",
      "Change adoption metrics and reinforcement systems",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Presentation Public Speaking Conducting Business Meeting", "pp"),
    name: "Presentation, Public Speaking & Conducting Business Meeting",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Deliver compelling presentations, speak confidently in public settings, and facilitate effective business meetings. This course covers slide design principles, storytelling structure, vocal techniques, body language, agenda management, and the facilitation skills that turn meetings into productive decision-making sessions.",
    topics: [
      "Slide design principles and visual storytelling",
      "Vocal techniques and body language for confidence",
      "Meeting agenda design and time management",
      "Facilitation skills for productive decision making",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Time Management", "pp"),
    name: "Time Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Maximise personal and team productivity through prioritisation, delegation, calendar management, and productivity system design. This course teaches the Eisenhower matrix, Pomodoro technique, batch processing, and the boundary-setting practices that prevent burnout while maintaining high output.",
    topics: [
      "Prioritisation with the Eisenhower matrix and ABC method",
      "Delegation techniques and task distribution",
      "Calendar management and meeting reduction strategies",
      "Productivity systems and burnout prevention",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Managerial Supervisory Skill Development", "pp"),
    name: "Managerial / Supervisory Skill Development",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Build foundational managerial and supervisory skills for first-time and emerging leaders. This course covers transition from peer to boss, delegation, motivation techniques, discipline and counselling, performance monitoring, and the authority-confidence balance needed to lead effectively.",
    topics: [
      "Transition from peer to supervisor and authority building",
      "Delegation, motivation, and accountability frameworks",
      "Discipline, counselling, and difficult conversation skills",
      "Performance monitoring and daily management routines",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Human Capital Statement and Compensation Management", "pp"),
    name: "Human Capital Statement and Compensation Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Design human capital statements and compensation structures that attract, retain, and motivate talent. This course covers job evaluation, pay band design, incentive schemes, benefits administration, and the HR metrics that demonstrate the ROI of people investment to factory leadership.",
    topics: [
      "Job evaluation and pay band architecture",
      "Incentive scheme design for production and staff roles",
      "Benefits administration and statutory compliance",
      "Human capital metrics and people investment ROI",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Strategic Marketing for Sales Excellence", "pp"),
    name: "Strategic Marketing for Sales Excellence",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Align marketing strategy with sales execution to drive revenue growth. This course covers market segmentation, value proposition design, competitive positioning, marketing-sales integration, lead generation, and the campaign analytics that prove marketing contribution to the bottom line.",
    topics: [
      "Market segmentation and target account selection",
      "Value proposition design and competitive positioning",
      "Marketing-sales integration and lead handoff",
      "Campaign analytics and revenue attribution",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Brand and Retail Marketing", "pp"),
    name: "Brand and Retail Marketing",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Build apparel brands and execute retail marketing strategies that connect with consumers. This course covers brand architecture, visual identity, retail operations, visual merchandising, store experience design, and the omnichannel strategies that unify physical and digital retail touchpoints.",
    topics: [
      "Brand architecture and visual identity systems",
      "Retail operations and store management fundamentals",
      "Visual merchandising and customer experience design",
      "Omnichannel strategy for unified retail presence",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
  {
    slug: s("Office Inventory Store Management", "pp"),
    name: "Office & Inventory (Store) Management",
    programme: "professional-proficiency",
    programmeLabel: "Professional Proficiency Incubator",
    description:
      "Manage office administration and store inventory with systems that ensure accuracy, security, and efficiency. This course covers filing and documentation, administrative workflow, inventory control methods, store layout optimisation, stock taking, and the technology tools that digitise store operations.",
    topics: [
      "Office administration and documentation systems",
      "Administrative workflow and process standardisation",
      "Inventory control methods and stock accuracy",
      "Store layout optimisation and digitisation tools",
    ],
    duration: "6 hours",
    audience: "Supervisors, managers, and department heads",
  },
];

/* ═══════════════════════════════════════════════════════════════
   Sustainability Management — 5 sustainability courses
   ═══════════════════════════════════════════════════════════════ */
export const sustainabilityCourses: CourseDetail[] = [
  {
    slug: s("Sustainable Use of Resources", "sms"),
    name: "Sustainable Use of Resources",
    programme: "sustainability",
    programmeLabel: "Sustainability Management",
    description:
      "Optimise resource consumption in textile manufacturing through systematic water, energy, and raw material efficiency programmes. This course covers audit methodologies, benchmarking against international standards, reduction strategy design, and ROI analysis for sustainable operations in Bangladesh factories.",
    topics: [
      "Water audit and consumption benchmarking in textile processing",
      "Energy efficiency in dyeing, finishing, and garment making",
      "Raw material optimisation and waste stream reduction",
      "ROI modelling for sustainability investment decisions",
    ],
    duration: "3 days",
    audience: "Factory sustainability officers, EHS managers, and compliance heads",
  },
  {
    slug: s("Sustainable Chemicals and Environmental Management", "sms"),
    name: "Sustainable Chemicals and Environmental Management",
    programme: "sustainability",
    programmeLabel: "Sustainability Management",
    description:
      "Implement ZDHC-aligned chemical management and environmental stewardship systems in textile factories. This course covers MRSL compliance auditing, chemical inventory digitisation, wastewater treatment innovation, and environmental impact reduction aligned with international buyer requirements.",
    topics: [
      "ZDHC MRSL compliance and chemical inventory digitisation",
      "Wastewater treatment innovation and ZLD pathways",
      "Environmental impact assessment and reduction strategies",
      "Buyer compliance coordination and documentation systems",
    ],
    duration: "3 days",
    audience: "Chemical managers, EHS officers, and compliance professionals",
  },
  {
    slug: s("Safety Management", "sms"),
    name: "Safety Management",
    programme: "sustainability",
    programmeLabel: "Sustainability Management",
    description:
      "Establish comprehensive occupational safety management systems tailored to Bangladesh textile factory conditions. This course covers hazard identification, risk assessment methodologies, PPE protocol design, emergency response planning, and compliance with Bangladesh labour safety standards and international buyer codes.",
    topics: [
      "Hazard identification and risk assessment methodologies",
      "PPE protocol design and enforcement systems",
      "Emergency response planning and drill management",
      "Bangladesh labour safety compliance and buyer code alignment",
    ],
    duration: "3 days",
    audience: "Safety officers, HR managers, and factory management",
  },
  {
    slug: s("Compliance Social and Materials Management", "sms"),
    name: "Compliance (Social & Materials) Management",
    programme: "sustainability",
    programmeLabel: "Sustainability Management",
    description:
      "Navigate social and materials compliance requirements including WRAP, BSCI, SA8000, and OEKO-TEX standards. This course covers audit preparation, documentation management, corrective action planning, and continuous improvement frameworks that maintain factory approval status with major international buyers.",
    topics: [
      "WRAP, BSCI, SA8000, and OEKO-TEX standard requirements",
      "Audit preparation and documentation management",
      "Corrective action planning and root-cause analysis",
      "Continuous improvement and re-audit readiness frameworks",
    ],
    duration: "3 days",
    audience: "Compliance managers, HR heads, and factory leadership",
  },
  {
    slug: s("Sustainability Management System", "sms"),
    name: "Sustainability Management System",
    programme: "sustainability",
    programmeLabel: "Sustainability Management",
    description:
      "Design and implement integrated sustainability management systems aligned with international ESG standards. This course covers carbon accounting, water stewardship, circular economy model design, stakeholder engagement, and the reporting frameworks that demonstrate sustainability leadership to global buyers and investors.",
    topics: [
      "Carbon accounting and greenhouse gas inventory methodology",
      "Water stewardship and circular economy model design",
      "ESG reporting framework and disclosure standards",
      "Stakeholder engagement and sustainability communication",
    ],
    duration: "3 days",
    audience: "Sustainability directors, strategy heads, and senior management",
  },
];

/* ═══════════════════════════════════════════════════════════════
   InternPro — Professional Internship Programme
   ═══════════════════════════════════════════════════════════════ */
export const internProCourses: CourseDetail[] = [
  {
    slug: s("InternPro Professional Internship Programme", "intern"),
    name: "InternPro Professional Internship Programme",
    programme: "internpro",
    programmeLabel: "InternPro",
    description:
      "Structured professional development programme for outgoing textile engineering students bridging academic knowledge with industry practice. InternPro provides organisational form, proper guidelines, and substantive learning experiences on relative fields to help interns be ready for the existing challenges of the textile and apparel industry in Bangladesh.",
    topics: [
      "Professional skill development through structured internship guidelines",
      "Real-world industry exposure and factory floor experience",
      "Organisational form and mentorship assignment",
      "Career readiness and industry challenge preparation",
    ],
    duration: "Internship period",
    audience: "Outgoing bachelor degree students of textile engineering",
  },
];

/* ═══════════════════════════════════════════════════════════════
   PPI Workshops — 5 focused professional workshops
   ═══════════════════════════════════════════════════════════════ */
export const ppiWorkshopCourses: CourseDetail[] = [
  {
    slug: s("Career Planning and Opportunities", "ppi"),
    name: "Career Planning and Opportunities",
    programme: "ppi-workshop",
    programmeLabel: "PPI Workshop",
    description:
      "Strategic career planning workshop for textile professionals at all stages. This workshop covers self-assessment methodologies, industry opportunity mapping, skill gap analysis, professional networking strategies, and the development of actionable career roadmaps for advancement in Bangladesh's textile sector.",
    topics: [
      "Self-assessment and competency profiling methodologies",
      "Industry opportunity mapping and role trajectory analysis",
      "Skill gap identification and development planning",
      "Professional networking and mentorship engagement strategies",
    ],
    duration: "1 day",
    audience: "Textile professionals at all career stages",
  },
  {
    slug: s("Knit Dyeing Production and Troubleshooting", "ppi"),
    name: "Knit Dyeing Production & Troubleshooting",
    programme: "ppi-workshop",
    programmeLabel: "PPI Workshop",
    description:
      "Intensive workshop addressing common and complex knit dyeing production challenges. This course covers defect root-cause analysis, recipe optimisation techniques, machine setting calibration, shade matching troubleshooting, and the systematic protocols that minimise reprocessing and right-first-time improvement.",
    topics: [
      "Common knit dyeing defects and root-cause analysis",
      "Recipe optimisation and lab-to-bulk translation",
      "Machine setting calibration and process parameter control",
      "Shade matching troubleshooting and reprocessing reduction",
    ],
    duration: "1 day",
    audience: "Dyeing supervisors, technicians, and production managers",
  },
  {
    slug: s("Commercial and Logistics in Merchandising", "ppi"),
    name: "Commercial & Logistics in Merchandising",
    programme: "ppi-workshop",
    programmeLabel: "PPI Workshop",
    description:
      "Master the commercial and logistics dimensions of apparel merchandising that determine order profitability and on-time delivery. This workshop covers accurate costing methodologies, buyer negotiation frameworks, shipping documentation, Incoterms selection, and the order follow-up workflows that prevent delays and disputes.",
    topics: [
      "Accurate costing methodologies and margin protection",
      "Buyer negotiation frameworks and terms management",
      "Shipping documentation and Incoterms application",
      "Order follow-up workflows and delay prevention systems",
    ],
    duration: "1 day",
    audience: "Merchandisers, commercial managers, and logistics coordinators",
  },
  {
    slug: s("Towards Zero Defect Quality in Textile and Apparel", "ppi"),
    name: "Towards Zero Defect Quality in Textile & Apparel",
    programme: "ppi-workshop",
    programmeLabel: "PPI Workshop",
    description:
      "Quality excellence workshop targeting zero-defect manufacturing through statistical process control, inline inspection system design, and quality culture building. This course teaches participants to identify defect sources upstream, implement preventive controls, and create accountability systems that drive continuous quality improvement.",
    topics: [
      "Statistical process control for textile manufacturing",
      "Inline inspection system design and checkpoint optimisation",
      "Defect source identification and upstream prevention",
      "Quality culture building and accountability system design",
    ],
    duration: "1 day",
    audience: "Quality managers, inspectors, and production supervisors",
  },
  {
    slug: s("Factory Sourcing and Order Follow Up", "ppi"),
    name: "Factory Sourcing & Order Follow Up",
    programme: "ppi-workshop",
    programmeLabel: "PPI Workshop",
    description:
      "Comprehensive workshop on factory sourcing evaluation and order management for merchandisers and buying office professionals. This course covers vendor assessment criteria, capacity verification methods, timeline management, communication protocols with buyers, and the documentation systems that ensure transparent order execution from confirmation to shipment.",
    topics: [
      "Vendor assessment criteria and capability scoring",
      "Capacity verification and workload balancing methods",
      "Timeline management and critical path scheduling",
      "Buyer communication protocols and documentation systems",
    ],
    duration: "1 day",
    audience: "Sourcing managers, merchandisers, and buying office staff",
  },
];

/* ═══════════════════════════════════════════════════════════════
   Other Trainings — 5 specialised advanced courses
   ═══════════════════════════════════════════════════════════════ */
export const otherTrainingCourses: CourseDetail[] = [
  {
    slug: s("Managing Dyehouses Towards Zero", "ot"),
    name: "Managing Dyehouses Towards Zero",
    programme: "other-training",
    programmeLabel: "Other Training",
    description:
      "Advanced dyehouse management certification programme targeting zero-liquid-discharge and near-zero waste operations. This course covers water recycling system design, chemical optimisation protocols, energy efficiency in dyeing machinery, and the sustainable process innovations that reduce environmental footprint while maintaining colour quality and production throughput.",
    topics: [
      "Zero-liquid-discharge strategy and water recycling design",
      "Chemical optimisation and auxiliaries reduction protocols",
      "Energy efficiency in jet, beam, and winch dyeing machinery",
      "Sustainable process innovation and environmental footprint reduction",
    ],
    duration: "3 days",
    audience: "Dyehouse managers, technical heads, and sustainability officers",
  },
  {
    slug: s("Testing of Light Fastness and Weathering of Textile", "ot"),
    name: "Testing of Light Fastness & Weathering of Textile",
    programme: "other-training",
    programmeLabel: "Other Training",
    description:
      "Specialised training on colour fastness and weathering testing for textile quality assurance laboratories. This course covers AATCC and ISO test methods, xenon arc exposure protocols, Q-SUN accelerated weathering operations, result interpretation against buyer standards, and compliance reporting for export shipments.",
    topics: [
      "AATCC and ISO colour fastness test methods",
      "Xenon arc exposure and Q-SUN operations",
      "Weathering result interpretation and buyer standard alignment",
      "Laboratory quality assurance and compliance reporting",
    ],
    duration: "2 days",
    audience: "Laboratory technicians, QA managers, and compliance officers",
  },
  {
    slug: s("AATCC International Training Program on Evaluating Colorfastness and Physical Properties", "ot"),
    name: "AATCC International Training Program on Evaluating Colorfastness & Physical Properties",
    programme: "other-training",
    programmeLabel: "Other Training",
    description:
      "Internationally recognised AATCC training programme on textile testing standards for colourfastness evaluation and physical property measurement. This course covers standardised test protocols, equipment calibration, result documentation, and the laboratory management practices that ensure consistent, reproducible testing outcomes aligned with global buyer expectations.",
    topics: [
      "AATCC standardised colourfastness test protocols",
      "Physical property measurement and equipment calibration",
      "Result documentation and data management systems",
      "Laboratory quality management and reproducibility assurance",
    ],
    duration: "3 days",
    audience: "Laboratory managers, QA professionals, and technical auditors",
  },
  {
    slug: s("Managing Cotton", "ot"),
    name: "Managing Cotton",
    programme: "other-training",
    programmeLabel: "Other Training",
    description:
      "Comprehensive cotton management training covering fibre quality assessment, contamination control, mixing strategies, and spinning preparation optimisation. This course addresses the unique challenges of Bangladesh's cotton supply chain from ginning through spinning, including supplier evaluation, stock management, and cost optimisation strategies.",
    topics: [
      "Cotton fibre quality assessment and HVI testing",
      "Contamination control and mixing strategy optimisation",
      "Spinning preparation and process parameter management",
      "Supplier evaluation, stock management, and cost optimisation",
    ],
    duration: "2 days",
    audience: "Spinning managers, cotton procurement heads, and technical staff",
  },
  {
    slug: s("FINMAN Cost and Financial Management", "ot"),
    name: "FINMAN — Cost and Financial Management for Non-Finance Managers",
    programme: "other-training",
    programmeLabel: "Other Training",
    description:
      "Financial literacy programme designed for textile professionals without formal finance training. This course covers cost centre management, departmental budgeting, break-even analysis, P&L statement interpretation, margin protection strategies, and the financial controls that enable non-finance managers to make commercially sound operational decisions.",
    topics: [
      "Cost centre management and departmental budgeting",
      "Break-even analysis and contribution margin calculation",
      "P&L interpretation and financial ratio analysis",
      "Margin protection strategies and commercial decision-making",
    ],
    duration: "2 days",
    audience: "Department heads, production managers, and non-finance professionals",
  },
];

/* ─── Aggregates ─── */
export const allCourses: CourseDetail[] = [
  ...blackBeltCourses,
  ...factorySkillsCourses,
  ...professionalProficiencyCourses,
  ...sustainabilityCourses,
  ...internProCourses,
  ...ppiWorkshopCourses,
  ...otherTrainingCourses,
];

export function findCourseBySlug(slug: string): CourseDetail | undefined {
  return allCourses.find((c) => c.slug === slug);
}

export function getRelatedCourses(slug: string, limit = 3): CourseDetail[] {
  const course = findCourseBySlug(slug);
  if (!course) return [];
  return allCourses
    .filter((c) => c.programme === course.programme && c.slug !== slug)
    .slice(0, limit);
}

export function getCoursesByProgramme(programme: CourseDetail["programme"]): CourseDetail[] {
  return allCourses.filter((c) => c.programme === programme);
}